<script lang="ts">
import { computed, defineComponent, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import LoaderView from '@/components/LoaderView.vue'
import { formatPhoneNumber } from '@/utils/phoneNumberValidator'
import LocaleComponent from '@/components/LocaleComponent.vue'
import {
  CheckNumberBlocking,
  CheckNumberRegistration,
  CheckStatusOfAnIndividual,
  GetSendOtpCode,
  GetVerifyOtpCode,
  GetVerifyPin,
} from '@/services/api/services'
import StatusView from '@/components/StatusView.vue'
import { usePhoneStore } from '@/stores/phoneStore'
import { useResidentStore } from '@/stores/residentStore'
import { useLogoStore } from '@/stores/logoStore'
import CustomInput from '@/components/input-label-button/CustomInput.vue'
import CustomButton from '@/components/input-label-button/CustomButton.vue'
import RadioButton from '@/components/input-label-button/RadioButton.vue'

export default defineComponent({
  name: 'PhoneNumberForm',
  components: {
    RadioButton,
    CustomButton,
    CustomInput,
    LocaleComponent,
    LoaderView,
    StatusView,
  },
  props: {
    msg: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const logoStore = useLogoStore()
    if (!logoStore.isStatusViewInitialized) {
      logoStore.isStatusViewInitialized = true
    }

    const form = ref(true)
    const formData = reactive({
      client_ctn: '',
      client_pin: '',
      client_sms: '',
    })
    const isPin = ref(true)
    const pinOrSms = ref('pin')
    const isSmsShow = ref(false)
    const showSmsInput = ref(false)

    const loading = ref(false)
    const loader = reactive({
      title: '',
      description: '',
    })
    const pinAttempts = ref(3)
    const smsAttempts = ref(3)

    const statusView = reactive({
      show: false,
      title: '',
      description: '',
      restartDecs: '',
      status: '',
      redirectToMyBeeline: false,
      restartIdentification: false,
      siteUnavailable: false,
    })

    const errors = reactive({
      ctnError: '',
      pinError: '',
      smsError: '',
    })

    const timer = ref(60)
    const timerActive = ref(false)
    let _timer: ReturnType<typeof setTimeout> | null = null

    const sendSms = async () => {
      showSmsInput.value = true
      timer.value = 60
      timerActive.value = true
      if (_timer) clearInterval(_timer)
      _timer = setInterval(() => {
        timer.value--
        if (timer.value <= 0) {
          timerActive.value = false
          clearInterval(_timer as unknown as number)
        }
      }, 1000)

      try {
        const ctn_formatted = ctnFormatted(formData.client_ctn)
        const response = await GetSendOtpCode(ctn_formatted.slice(1))
        if (response && response.data) {
          timerActive.value = false
          clearInterval(_timer)
        }
      } catch (e) {
        form.value = false
        const isSiteUnavailable = (e as any)?.response?.status === 540
        const title = t(isSiteUnavailable ? 'statusView.errTitle.siteUnavailable' : 'statusView.errTitle.title1')
        const desc = isSiteUnavailable ? t('statusView.errDesc.siteUnavailable') : (e as any)?.response?.data?.message
        const restartDecs = t('registrationNumber.btnText')
        statusProcessing('error', title, desc, restartDecs, false, true, isSiteUnavailable)
        timerActive.value = false
        clearInterval(_timer)
      }
    }

    const enabled = computed(() => {
      if (pinOrSms.value === 'pin') {
        return (
          formatPhoneNumber(formData.client_ctn).length >= 16 &&
          formData.client_pin.length >= 4
        )
      } else {
        return (
          formatPhoneNumber(formData.client_ctn).length >= 16 &&
          formData.client_sms.length >= 6
        )
      }
    })

    const enabledSms = computed(() => {
      return formatPhoneNumber(formData.client_ctn).length >= 16
    })

    const ctnFormatted = (ctn: string) => {
      return ctn.replace(/\s+/g, '')
    }

    const setPhone = async () => {
      const ctn_formatted = ctnFormatted(formData.client_ctn)
      const phoneStore = usePhoneStore()
      phoneStore.setCtn(ctn_formatted)
    }

    const statusProcessing = (
      statusViewStatus: string,
      title: string,
      desc: string,
      restartDecs: string,
      redirectToMyBeeline: boolean,
      restartIdentification: boolean,
      siteUnavailable = false
    ) => {
      loading.value = false
      statusView.show = true
      statusView.status = statusViewStatus
      statusView.title = title
      statusView.description = desc
      statusView.restartDecs = restartDecs
      statusView.redirectToMyBeeline = redirectToMyBeeline
      statusView.restartIdentification = restartIdentification
      statusView.siteUnavailable = siteUnavailable
    }

    const processingNumberBlocking = async (block: string, ctn: string) => {
      form.value = false
      if (block === 'OFFICE') {
        const title = t('deviceRegistration.messages.errors.blockedCase.title')
        const desc = t('deviceRegistration.messages.errors.blockedCase.decs1')
        const restartDecs = t('deviceRegistration.messages.backBtn')
        statusProcessing('blockedDevice', title, desc, restartDecs, false, true)
      } else if (block === 'CONTACT_AUTHORITY') {
        const title = t('deviceRegistration.messages.errors.blockedCase.title')
        const desc = t('deviceRegistration.messages.errors.blockedCase.decs2')
        const restartDecs = t('deviceRegistration.messages.backBtn')
        statusProcessing('blockedDevice', title, desc, restartDecs, false, true)
      } else if (block === 'IIN_INPUT') {
        loading.value = false
        await setPhone()
        setTimeout(() => {
          router.push({
            path: '/device-registration',
            query: {
              ctn,
            },
          })
        }, 500)
      } else if (block === 'SUCCESS' || block === 'UNDEFINED') {
        const title = t(
          'registrationNumber.messages.regNumberAndDeviceAvailableTitle'
        )
        const desc = t(
          'registrationNumber.messages.regNumberAndDeviceAvailableDesc'
        )
        const restartDecs = t('registrationNumber.btnBack')
        statusProcessing('success', title, desc, restartDecs, true, false)
      }
    }

    const setResident = async (isResident: boolean) => {
      const residentStore = useResidentStore()
      residentStore.setResident(isResident)
    }

    const checkingNumberBlocking = async (ctn: string) => {
      try {
        const response_blocking = await CheckNumberBlocking(ctn)
        if (response_blocking && response_blocking.data.result) {
          try {
            const resp_isResident = await CheckStatusOfAnIndividual(ctn)
            if (resp_isResident) {
              await setResident(resp_isResident.data.resident)
              await processingNumberBlocking(response_blocking.data.result, ctn)
            }
          } catch (e) {
            const title = t('statusView.errTitle.title1')
            const desc = t('statusView.errDesc.decs2')
            const restartDecs = t('registrationNumber.btnText')
            statusProcessing('error', title, desc, restartDecs, false, true)
          }
        }
      } catch (e) {
        const title = t('statusView.errTitle.title1')
        const desc = t('statusView.errDesc.decs2')
        const restartDecs = t('registrationNumber.btnText')
        statusProcessing('error', title, desc, restartDecs, false, true)
      }
    }

    const getCheckNumberRegistration = async (ctnFormatted: string) => {
      loader.title = t('phoneNumberForm.loaderTitle')
      loader.description = t('phoneNumberForm.loaderDesc')
      form.value = false
      loading.value = true
      try {
        const response = await CheckNumberRegistration(ctnFormatted.slice(1))
        if (response) {
          if (response.data.status === 'UNREGISTERED') {
            await setPhone()
            setTimeout(() => {
              router.push({
                path: '/number-registration',
                query: {
                  ctn: ctnFormatted,
                },
              })
            }, 3000)
          } else if (response.data.status === 'REGISTERED') {
            await checkingNumberBlocking(ctnFormatted.slice(1))
          } else if (
            response.data.status === 'ANNULLED' ||
            response.data.status === 'FORTE_OR_IZI'
          ) {
            loading.value = false
            form.value = true
            errors.ctnError = statusView.description = t(
              'registrationNumber.messages.anotherNumberTitle'
            )
          }
        }
      } catch (err) {
        const title = t('statusView.errTitle.title1')
        const desc = t('statusView.errDesc.decs2')
        const restartDecs = t('registrationNumber.btnText')
        statusProcessing('error', title, desc, restartDecs, false, true)
      }
    }

    const getCheckAll = async () => {
      loader.title = t(pinOrSms.value === 'pin' ? 'phoneNumberForm.pinLoader' : 'phoneNumberForm.smsLoader')
      loader.description = t('phoneNumberForm.loaderDesc')
      form.value = false
      loading.value = true

      const ctn_formatted = ctnFormatted(formData.client_ctn)
      if (pinOrSms.value === 'pin') {
        const body = {
          pin1: formData.client_pin,
          msisdn: ctn_formatted.slice(1),
        }

        try {
          const response = await GetVerifyPin(body)
          if (response && response.data) {
            pinAttempts.value = response.data?.attempt
            if (response.data.result && pinAttempts.value > 1) {
              await getCheckNumberRegistration(ctn_formatted)
            } else if (pinAttempts.value <= 0) {
              form.value = false
              let title, desc, restartDecs
              if (response.data.blockType === 'SMS') {
                title = t('statusView.phoneNumberForm.sms.title')
                desc = t('statusView.phoneNumberForm.sms.description')
                restartDecs = t('registrationNumber.btnText')
              } else if (response.data.blockType === 'PIN') {
                title = t('statusView.phoneNumberForm.pin.title')
                desc = t('statusView.phoneNumberForm.pin.description')
                restartDecs = t('registrationNumber.btnText')
              } else {
                title = t('statusView.phoneNumberForm.pinAndSms.title')
                desc = t('statusView.phoneNumberForm.pinAndSms.description')
                restartDecs = t('registrationNumber.btnText')
              }
              statusProcessing(
                'fatalError',
                title,
                desc,
                restartDecs,
                false,
                true,
              )
            } else {
              errors.pinError = t('phoneNumberForm.pinErrorAttempts', {
                count: pinAttempts.value,
              })
              loading.value = false
              form.value = true
            }
          }
        } catch (e) {
          const isSiteUnavailable = (e as any)?.response?.status === 540
          const title = t(isSiteUnavailable ? 'statusView.errTitle.siteUnavailable' : 'statusView.errTitle.title1')
          const desc = isSiteUnavailable ? t('statusView.errDesc.siteUnavailable') : (e as any)?.response?.data?.message
          const restartDecs = t('registrationNumber.btnText')
          statusProcessing('error', title, desc, restartDecs, false, true, isSiteUnavailable)
        }
      } else {
        const body = {
          otp: formData.client_sms,
        }

        try {
          const response = await GetVerifyOtpCode(ctn_formatted.slice(1), body)
          if (response && response.data) {
            smsAttempts.value = response.data?.attempt
            if (response.data.verified && smsAttempts.value > 1) {
              await getCheckNumberRegistration(ctnFormatted(formData.client_ctn))
            } else if (smsAttempts.value <= 0) {
              form.value = false
              let title, desc, restartDecs
              if (response.data.blockType === 'SMS') {
                title = t('statusView.phoneNumberForm.sms.title')
                desc = t('statusView.phoneNumberForm.sms.description')
                restartDecs = t('registrationNumber.btnText')
              } else if (response.data.blockType === 'PIN') {
                title = t('statusView.phoneNumberForm.pin.title')
                desc = t('statusView.phoneNumberForm.pin.description')
                restartDecs = t('registrationNumber.btnText')
              } else {
                title = t('statusView.phoneNumberForm.pinAndSms.title')
                desc = t('statusView.phoneNumberForm.pinAndSms.description')
                restartDecs = t('registrationNumber.btnText')
              }
              statusProcessing(
                'fatalError',
                title,
                desc,
                restartDecs,
                false,
                true,
              )
            } else {
              errors.smsError = t('phoneNumberForm.smsErrorAttempts', {
                count: smsAttempts.value,
              })
              loading.value = false
              form.value = true
            }
          }
        } catch (e) {
          const isSiteUnavailable = (e as any)?.response?.status === 540
          const title = t(isSiteUnavailable ? 'statusView.errTitle.siteUnavailable' : 'statusView.errTitle.title1')
          const desc = isSiteUnavailable ? t('statusView.errDesc.siteUnavailable') : (e as any)?.response?.data?.message
          const restartDecs = t('registrationNumber.btnText')
          statusProcessing('error', title, desc, restartDecs, false, true, isSiteUnavailable)
        }
      }
    }

    const validatePhoneNumber = (tel: string) => {
      errors.ctnError =
        tel === '' || tel === null || tel.length === 16
          ? ''
          : t('phoneNumberForm.ctnError')
    }

    const handleChange = (value: string) => {
      formData.client_ctn = value
      validatePhoneNumber(value)
    }

    const ensureCountryCode = () => {
      if (!formData.client_ctn.startsWith('+7')) {
        formData.client_ctn = `+7${formData.client_ctn.replace(/^\+7/, '')}`
      }
    }

    const handleChangePin = (pin: string) => {
      errors.pinError = pin.length >= 4 ? '' : t('phoneNumberForm.pinLengthErr')
    }
    const handleChangeSms = (sms: string) => {
      errors.smsError = sms.length >= 4 ? '' : t('phoneNumberForm.smsLengthErr')
    }
    const updatePropValue = (newValue: boolean) => {
      statusView.show = newValue
      form.value = true
    }

    watch(pinOrSms, (value) => {
      isPin.value = value === 'resident'
      isSmsShow.value = value === 'sms'
    })

    return {
      form,
      formData,
      loading,
      loader,
      statusView,
      errors,
      enabled,
      getCheckAll,
      handleChange,
      ensureCountryCode,
      handleChangePin,
      updatePropValue,
      pinOrSms,
      handleChangeSms,
      isSmsShow,
      sendSms,
      isPin,
      showSmsInput,
      timerActive,
      timer,
      enabledSms,
    }
  },
})
</script>

<template>
  <div>
    <div v-if="form" class="block">
      <div class="title-desc">
        <h2>{{ msg }}</h2>
        <p>{{ $t('phoneNumberForm.description') }}</p>
      </div>
      <div class="form">
        <div class="gap-24">
          <CustomInput
            id="phone"
            v-model="formData.client_ctn"
            name="phone"
            :model-value="formData.client_ctn"
            input-mode="numeric"
            maxlength="16"
            type="text"
            autocomplete="off"
            custom-class="input-style"
            error-class="error-styles"
            :has-error="errors.ctnError.length > 0"
            :error-message="errors.ctnError"
            :label="$t('phoneNumberForm.label')"
            :only-numbers="true"
            format="phone"
            @update:model-value="handleChange"
            @blur="ensureCountryCode"
          />
          <div class="radio-btns">
            <RadioButton
              :label="$t('phoneNumberForm.pinOption')"
              value="pin"
              :model-value="pinOrSms"
              @update:modelValue="pinOrSms = $event"
            ></RadioButton>

            <RadioButton
              :label="$t('phoneNumberForm.smsOption')"
              value="sms"
              :model-value="pinOrSms"
              @update:modelValue="pinOrSms = $event"
            ></RadioButton>
          </div>

          <CustomInput
            v-if="pinOrSms === 'pin'"
            id="pin"
            v-model="formData.client_pin"
            name="pin"
            :model-value="formData.client_pin"
            input-mode="numeric"
            maxlength="4"
            type="text"
            autocomplete="off"
            custom-class="input-style"
            error-class="error-styles"
            :has-error="errors.pinError.length > 0"
            :error-message="errors.pinError"
            :label="$t('phoneNumberForm.pinLabel')"
            :only-numbers="true"
            format="pin"
            @update:model-value="handleChangePin"
          />

          <div v-if="showSmsInput">
            <CustomInput
              v-if="pinOrSms === 'sms'"
              id="sms"
              v-model="formData.client_sms"
              name="sms"
              :model-value="formData.client_sms"
              input-mode="numeric"
              maxlength="6"
              type="text"
              autocomplete="off"
              custom-class="input-style"
              error-class="error-styles"
              :has-error="errors.smsError.length > 0"
              :error-message="errors.smsError"
              :label="$t('phoneNumberForm.smsLabel')"
              :only-numbers="true"
              format="sms"
              @update:model-value="handleChangeSms"
            />

            <div v-if="pinOrSms === 'sms'" class="timer_container">
              <span v-if="timerActive" class="timer">
                {{
                  Math.floor(timer / 60)
                    .toString()
                    .padStart(2, '0')
                }}:{{ (timer % 60).toString().padStart(2, '0') }}
              </span>
              <a v-else class="link" @click="sendSms">{{
                $t('phoneNumberForm.resendSmsCode')
              }}</a>
            </div>
          </div>
        </div>

        <div class="gap-48">
          <CustomButton
            v-if="pinOrSms === 'pin' || (pinOrSms === 'sms' && showSmsInput)"
            :title="$t('phoneNumberForm.btnText')"
            custom-btn-class="primary-button"
            :is-disabled="!enabled"
            @click="getCheckAll"
          />
          <CustomButton
            v-if="
              isSmsShow && pinOrSms === 'sms' && !showSmsInput && !timerActive
            "
            :is-disabled="!enabledSms"
            :title="$t('phoneNumberForm.sendSms')"
            custom-btn-class="primary-button"
            @click="sendSms"
          />

          <LocaleComponent />
        </div>
      </div>
    </div>
    <LoaderView
      v-if="loading"
      :loading="loading"
      :title="loader.title"
      :description="loader.description"
    />

    <StatusView
      v-if="statusView.show"
      :title="statusView.title"
      :restart-identification="statusView.restartIdentification"
      :restart-decs="statusView.restartDecs"
      :status-biometry="statusView.status"
      :description="statusView.description"
      :show-status-biometry="statusView.show"
      :no-access-to-camera="false"
      :is-redirect-to-my-beeline="statusView.redirectToMyBeeline"
      :site-unavailable="statusView.siteUnavailable"
      @update-prop="updatePropValue"
    />
  </div>
</template>

<style scoped lang="scss">
.block {
  display: flex;
  flex-direction: column;
  padding: 48px 50px 24px 50px;
  border-radius: 12px;
  gap: 32px;
  background: #ffffff;
  box-shadow: 0 4px 16px 0 #10083f1f;

  .title-desc {
    display: flex;
    flex-direction: column;
    gap: 8px;

    & h2 {
      font-size: 28px;
    }

    & p {
      font-size: 14px;
      color: var(--color-label-paragraph);
    }
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: 32px;

    .gap-48 {
      display: flex;
      flex-direction: column;
      gap: 48px;
    }

    .gap-24 {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
  }
}

.timer_container {
  display: flex;
  justify-content: center;
  padding-top: 16px;
  font-size: 14px;

  .timer {
    color: #696d86;
  }

  .link {
    color: #294acf;
    cursor: pointer;
  }
}

@media screen and (max-width: 500px) {
  .block {
    padding: 0;
    border: none;
    box-shadow: none;

    .title-desc {
      display: flex;
      align-items: center;

      & h2 {
        font-size: 24px;
      }
    }
  }
}

@media screen and (min-width: 560px) {
  .block {
    min-width: 520px;
  }
}

@media screen and (max-width: 500px) {
  .radio-btns {
    flex-direction: row !important;
  }
}

@media screen and (max-width: 366px) {
  .radio-btns {
    flex-direction: column !important;
  }
}
</style>
